import { FormBuilder } from '@angular/forms';
import { OptionModel } from '@features/admin/option-management/models/option.model';
import { PrincipalInvestigatorReportService } from '@features/reports/service/principal-investigator-report.service';
import { map, Observable, startWith, Subject, Subscription, takeUntil } from 'rxjs';
import { PrincipalInvestigatorReport, SearchPrincipalInvestigator } from '../models/principal-investigator-report';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { AppState } from '@core/app-state/reducers';
import { select, Store } from '@ngrx/store';
import { optionSelector } from '@features/admin/option-management/store/option.selectors';
import { OptionType } from '@features/admin/option-management/models/option-type.enum';
import { PrincipalInvestigatorReportList } from '../models/principal-investigator-report-list';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

@Component({
 selector: 'app-principal-investigator-report',
 templateUrl: './principal-investigator-report.component.html',
 styleUrls: ['./principal-investigator-report.component.scss']
})
export class PrincipalInvestigatorReportComponent implements OnInit, OnDestroy {
 @ViewChild(MatPaginator) paginator!: MatPaginator;
 @ViewChild(MatSort) sortHeader!: MatSort;
 private readonly STORAGE_KEY = 'piReport.filters.v1';
 subs$: Subscription[] = [];
 private destroy$ = new Subject<void>();
 // Title line required at top of page
 readonly title = 'Principal Investigator';
 institutions!: OptionModel[];
 filteredInstitutions$!: Observable<OptionModel[]>;
 tableLoading = false; // for table data fetch
 excelLoading = false; // for Excel export
 private destroyed$ = new Subject<void>();
  protocolStatusOptions: string[] = [];

 constructor(private store: Store<AppState>, public reportService: PrincipalInvestigatorReportService, public fb: FormBuilder) { }

 get form() { return this.reportService.form; }

 // Table
 displayedColumns = ['firstName', 'lastName', 'email', 'phone', 'institution'];
 data: PrincipalInvestigatorReport[] = [];
 total = 0;

 ngOnInit(): void {
 this.setFormOptions();
 this.restoreState();
 this.fetch();

 this.form.valueChanges
 .pipe(takeUntil(this.destroy$))
 .subscribe(() => {
 this.reportService.pageIndex = 1;
 this.reportService.save();
 });
 }

 private setFormOptions(): void {
 // institutions
 this.store.select(optionSelector.optionsByType(OptionType.institution))
 .pipe(takeUntil(this.destroy$))
 .subscribe((options: OptionModel[]) => {
 this.institutions = options ?? [];
 this.filteredInstitutions$ = this.form.controls.institutionId.valueChanges.pipe(
 startWith(null),
 map(value => typeof value === 'string' ? value : value?.name),
 map(name => name ? this._filter(name, this.institutions) : this.institutions.slice())
 );
 });

 // protocol statuses
 this.store.select(optionSelector.optionsByType(OptionType.protocolStatus))
   .pipe(takeUntil(this.destroy$))
   .subscribe((options: OptionModel[]) => {
     const names = (options || []).map(o => o.name).filter(n => !!n);
     this.protocolStatusOptions = names;
     // ensure defaults if control empty and no saved state yet
     const current = this.form.controls['protocolStatuses'].value as string[] | null;
     if (!current || current.length ===0) {
        const defaults = ['Active','Exempt'].filter(d => names.indexOf(d) !== -1);
       if (defaults.length) {
          this.form.patchValue({ protocolStatuses: defaults }, { emitEvent: false });
       }
    }
   });
 }

 private _filter(name: string, optionList: OptionModel[]): any {
 const filterValue = name.trim().toLowerCase();
 return optionList.filter(option => option.name.trim().toLowerCase().includes(filterValue));
 }

 onSortChange(sort: Sort) {
 this.reportService.handleSortChange(sort, () => this.fetch());
 }

 onPage(evt: PageEvent) {
 this.reportService.pageIndex = evt.pageIndex +1;
 this.reportService.pageSize  = evt.pageSize;
 this.saveState();
 this.fetch();
 }

 reset() {
 this.reportService.clear();
 this.fetch();
  }

 downloadExcel() {
 const dto = this.buildSearchDto(true);
 this.excelLoading = true;
 this.reportService.exportExcel(dto).subscribe({
 next: (blob) => {
 this.excelLoading = false;
 const date = new Date();
 const pad = (n: number) => n.toString().padStart(2, '0');
 const fileName = `IRB_PrincipalInvestigators_${pad(date.getMonth() + 1)}_${pad(date.getDate())}_${date.getFullYear()}.xlsx`;
 const url = URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = fileName;
 a.click();
 URL.revokeObjectURL(url);
 },
 error: () => (this.excelLoading = false)
 });
 }

 fetch() {
 const dto = this.buildSearchDto(false);
 this.tableLoading = true;
 this.reportService.getReport(dto).subscribe({
 next: (payload: PrincipalInvestigatorReportList) => {
 this.tableLoading = false;
 this.data  = payload?.piReport ?? [];
 this.total = payload?.totalCount ?? 0;
 },
 error: () => (this.tableLoading = false)
 });
 }

 displayFn(option: OptionModel | string | null): string {
 if (!option) return '';
 if (typeof option === 'string') return option;
 return option.name ?? '';
 }

 private buildSearchDto(forExcel: boolean): SearchPrincipalInvestigator {
 const raw = this.form.getRawValue();
 const instVal = raw.institutionId as any;
 const institutionId =
 typeof instVal === 'number'      ? instVal :
 instVal && typeof instVal.id === 'number' ? instVal.id :
 undefined; // ignore strings / null

 return {
 firstName: (raw.firstName || '').trim() || undefined,
 lastName: (raw.lastName || '').trim() || undefined,
 institutionId,
 userStatus: raw.userStatus || 'Active',
 protocolStatuses: (raw.protocolStatuses || ['Active','Exempt']) as string[],
 sort: this.reportService.sort,
 order: this.reportService.order,
 pageIndex: this.reportService.pageIndex,
 pageSize: this.reportService.pageSize,
 forExcel
 };
 }

 private saveState(): void {
 const raw = this.form.getRawValue();
 const inst = raw.institutionId as any;
 const institutionId =
 typeof inst === 'number' ? inst : (inst && typeof inst.id === 'number' ? inst.id : null);
 localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
 firstName: (raw.firstName || '').trim(),
 lastName: (raw.lastName || '').trim(),
 institutionId,
 userStatus: raw.userStatus || 'Active',
 protocolStatuses: (raw.protocolStatuses || ['Active','Exempt']) as string[],
 pageIndex: this.reportService.pageIndex,
 pageSize: this.reportService.pageSize,
 sort: this.reportService.sort,
 order: this.reportService.order
 }));
 }

 private restoreState(): void {
 const s = localStorage.getItem(this.STORAGE_KEY);
 if (!s) return;
 try {
 const state = JSON.parse(s);
 this.form.patchValue({
 firstName: state.firstName ?? '',
 lastName: state.lastName ?? '',
 institutionId: state.institutionId ?? null,
 userStatus: state.userStatus ?? 'Active',
 protocolStatuses: state.protocolStatuses?.length ? state.protocolStatuses : ['Active','Exempt']
 }, { emitEvent: false });
 this.reportService.pageIndex = state.pageIndex ?? 1;
 this.reportService.pageSize = state.pageSize ?? 10;
 this.reportService.sort = state.sort ?? 'LastName';
 this.reportService.order = state.order ?? 'asc';
 } catch { }
 }

 ngOnDestroy(): void {
 this.destroy$.next();
 this.destroy$.complete();
 }
}
